import React, { useEffect, useState, useRef } from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { OrganizationUserService, OrganizationUserVM } from "@propertelligent/client-api";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import InputField from "../../../newComponents/form-fields/InputField";
import apiHelper from "../../../utils/apiHelper";
import PhoneInput from "../../../newComponents/form-fields/PhoneInput";
import { getWhatsAppEnabled } from "../../../services/whatsApp.service";
import {
  getMyWhatsAppSender,
  getWhatsAppSenderStatusDisplay,
  registerMyWhatsAppSender,
  verifyMyWhatsAppSender,
  WhatsAppSenderStatusEnum,
  WhatsAppSenderStatusVM,
} from "../../../services/whatsAppSender.service";
import StatusTag from "../../common/StatusTag";
import Commonpopup from "../../../newComponents/Common-popup/CommonPopup";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import AddTraspButton from "../../firstTimeUserExperience/commonCom/AddTraspButton";
import { handleApiErrors } from "../../../utils/handleApiErrors";
import SimpleSpinner from "../../loader";
import { useFieldChangesSnapshot } from "../../../hooks/useFieldChangesSnapshot";
import UtilHelper from "../../../utils/UtilHelper";

interface EditProfileProps {
  isOpen?: boolean;
  onClose?: () => void;
  userId?: number; 
  refresh?: () => void;
}

const CONNECTABLE_STATUSES = new Set([
  WhatsAppSenderStatusEnum.None,
  WhatsAppSenderStatusEnum.Failed,
  WhatsAppSenderStatusEnum.Offline,
]);

const EditProfileModal = ({ isOpen, onClose, userId, refresh }: EditProfileProps) => {
  const [detailsReady, setDetailsReady] = useState(false);
  const [whatsAppEnabled, setWhatsAppEnabled] = useState(false);
  const [senderStatus, setSenderStatus] = useState<WhatsAppSenderStatusEnum>(
    WhatsAppSenderStatusEnum.None,
  );
  const [senderPhone, setSenderPhone] = useState("");
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const originalPhoneRef = useRef<string>("");

  const methods = useForm<OrganizationUserVM>({ mode: "onChange" });
  const { reset, control, formState: { isDirty, isValid }, watch } = methods;
  const profilePhone = watch("phoneNumber");

  const { hasFieldChanges, setSnapshot } = useFieldChangesSnapshot<{
    title?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }>(control, ["title", "firstName", "lastName", "email"]);

  const watchedPhoneNumber = useWatch({ control, name: "phoneNumber" });
  const phoneNumberChanged =
    UtilHelper.normalizePhoneDigits(watchedPhoneNumber) !==
    UtilHelper.normalizePhoneDigits(originalPhoneRef.current);

  const hasUnsavedChanges =
    isDirty && (hasFieldChanges || phoneNumberChanged);

  const otpMethods = useForm<{ code: string }>({
    defaultValues: { code: "" },
    mode: "onChange",
  });
  const otpCode = otpMethods.watch("code");

  const applySenderVm = (vm?: WhatsAppSenderStatusVM | null) => {
    setSenderStatus(vm?.senderStatus ?? WhatsAppSenderStatusEnum.None);
    if (typeof vm?.phoneNumber === "string") {
      setSenderPhone(vm.phoneNumber);
    }
  };

  useEffect(() => {
    if (!isOpen || !userId) {
      setDetailsReady(false);
      setIsOtpOpen(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setDetailsReady(false);
      const [res, waEnabled] = await Promise.all([
        apiHelper(
          OrganizationUserService.getApiOrganizationUser,
          { showNotification: false },
          userId
        ),
        getWhatsAppEnabled(),
      ]);

      if (cancelled) return;

      setWhatsAppEnabled(waEnabled);
      if (res?.data?.success) {
        const user = res.data.success as OrganizationUserVM;
        const phoneForInput = UtilHelper.toPhoneInputValue(user.phoneNumber || "");
        const hydrated = { ...user, phoneNumber: phoneForInput };
        originalPhoneRef.current = phoneForInput;
        setSnapshot({
          title: hydrated.title || "",
          firstName: hydrated.firstName || "",
          lastName: hydrated.lastName || "",
          email: hydrated.email || "",
        });
        reset(hydrated);
      }

      if (waEnabled) {
        const senderRes = await apiHelper(getMyWhatsAppSender, { showNotification: false });
        if (cancelled) return;
        applySenderVm(senderRes?.data?.success);
      }

      setDetailsReady(true);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, userId, reset, setSnapshot]);

  const onSubmit = async (formData: any) => {
    
    const cleanPayload = {
      id: formData.id,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      title: formData.title || "",
      organizationUserTypeId: formData.organizationUserTypeId,
      organizationId: formData.organizationId,
      organizationUserId: userId,
    };

    // 2. API Call
    const res = await apiHelper(
      OrganizationUserService.putApiOrganizationUser,
      { title: "Profile Updated", showNotification: true, successMessage: "Profile has been updated successfully." },
      String(userId),
      cleanPayload 
    );

    if (res?.data) {
      if (whatsAppEnabled) {
        const senderRes = await apiHelper(getMyWhatsAppSender, { showNotification: false });
        applySenderVm(senderRes?.data?.success);
      }
      refresh();
      onClose();
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const res = await apiHelper(registerMyWhatsAppSender, {
        showNotification: true,
        successMessage: "Verification code sent.",
        failureMessage: "Could not start WhatsApp connection.",
      });
      if (res?.errors) {
        handleApiErrors(res.errors, methods.setError);
        return;
      }
      const vm = res?.data?.success as WhatsAppSenderStatusVM | undefined;
      if (vm) {
        applySenderVm(vm);
        if (vm.senderStatus === WhatsAppSenderStatusEnum.Pending) {
          otpMethods.reset({ code: "" });
          setIsOtpOpen(true);
        }
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const senderRes = await apiHelper(getMyWhatsAppSender, { showNotification: false });
      applySenderVm(senderRes?.data?.success);
    } finally {
      setIsRefreshing(false);
    }
  };

  const openOtpPopup = () => {
    otpMethods.reset({ code: "" });
    setIsOtpOpen(true);
  };

  const closeOtpPopup = () => {
    setIsOtpOpen(false);
    otpMethods.reset({ code: "" });
  };

  const onVerify = async (data: { code: string }) => {
    const res = await apiHelper(
      verifyMyWhatsAppSender,
      {
        showNotification: true,
        successMessage: "WhatsApp is connected.",
        failureMessage: "Could not verify the code.",
      },
      data.code,
    );
    if (res?.errors) {
      handleApiErrors(res.errors, otpMethods.setError);
      return;
    }
    if (res?.data?.success) {
      applySenderVm(res.data.success);
      closeOtpPopup();
    }
  };

  const senderDisplay = getWhatsAppSenderStatusDisplay(senderStatus);
  const otpPhone = senderPhone || profilePhone || "this phone";
  const showConnect = CONNECTABLE_STATUSES.has(senderStatus);
  const showPendingActions = senderStatus === WhatsAppSenderStatusEnum.Pending;

  return (
    <>
    <RightSideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Basic Details"
      buttonlabel="Save"
      formId="edit-profile-form"
      isDirty={hasUnsavedChanges}
      isLoading={methods.formState.isSubmitting}
      isSubmitDisabled={
        !detailsReady ||
        methods.formState.isSubmitting ||
        !hasUnsavedChanges ||
        !isValid
      }
    >
      <FormProvider {...methods}>
        <form id="edit-profile-form" onSubmit={methods.handleSubmit(onSubmit)} className="p-3">
          {!detailsReady ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "40vh" }}
              data-testid="edit-profile-loading"
            >
              <SimpleSpinner myClassName="Icon40" />
            </div>
          ) : (
          <div className="row g-3">
            <div className="col-md-12">
              <InputField
                name="title"
                label="Title"
                placeholder="Enter title"
                onlyAlphabets
                validation={{
                  maxLength: {
                    value: 32,
                    message: "Title cannot exceed 32 characters",
                  },
                }}
              />
            </div>
            <div className="col-md-6">
              <InputField name="firstName" label="First Name" required requiredMessage="First name is required" validation={{minLength: { value: 2, message: "First name must be at least 2 characters long" }, maxLength: { value: 20, message: "First name cannot exceed 20 characters" } }} onlyAlphabets />
            </div>
            <div className="col-md-6">
              <InputField name="lastName" label="Last Name" required requiredMessage="Last name is required" validation={{minLength: { value: 2, message: "Last name must be at least 2 characters long" }, maxLength: { value: 20, message: "Last name cannot exceed 20 characters" } }} onlyAlphabets />
            </div>
            <div className="col-md-12">
              <InputField name="email" label="Email Address" disabled />
            </div>
            <div className="col-md-12">
              <PhoneInput
                key={whatsAppEnabled ? "phone-wa-all" : "phone-us-only"}
                name="phoneNumber"
                label="Contact No"
                required
                placeholder="Enter contact number"
                className="phone-input-custom"
                requiredMessage="Contact number is required"
                defaultCountry="us"
                onlyCountries={whatsAppEnabled ? [] : ["us"]}
              />
              {whatsAppEnabled && (
                <>
                  <p className="sub_text grey_subtext1 mb-0 mt-1">
                    Used as the WhatsApp Business sender for Messaging.
                  </p>
                  <p className="sub_text mb-1 mt-2">WhatsApp Business status</p>
                  <StatusTag
                    color={senderDisplay.color}
                    statusText={senderDisplay.label}
                    isLarge
                  />
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {showConnect && (
                      <AddTraspButton
                        type="button"
                        bname={isConnecting ? "Connecting..." : "Connect WhatsApp"}
                        disabled={isConnecting}
                        onClick={handleConnect}
                      />
                    )}
                    {showPendingActions && (
                      <>
                        <AddTraspButton
                          type="button"
                          bname="Enter verification code"
                          onClick={openOtpPopup}
                        />
                        <AddTraspButton
                          type="button"
                          bname={isRefreshing ? "Refreshing..." : "Refresh status"}
                          disabled={isRefreshing}
                          onClick={handleRefresh}
                        />
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          )}
        </form>
      </FormProvider>
    </RightSideModal>
    <Commonpopup
      isOpen={isOtpOpen}
      onClose={closeOtpPopup}
      component={
        <FormProvider {...otpMethods}>
          <form
            className="text-start"
            onSubmit={otpMethods.handleSubmit(onVerify)}
          >
            <p className="sub_heading mb-2">Verify your WhatsApp number</p>
            <p className="sub_text grey_subtext1 mb-3">
              {`Enter the code sent to ${otpPhone}`}
            </p>
            <InputField
              name="code"
              label="Verification code"
              required
              requiredMessage="Verification code is required."
              placeholder="Enter code"
              allowSpecialChars
            />
            <div className="d-flex justify-content-end align-items-center gap-2 pt-3">
              <AddTraspButton
                type="button"
                bname="Cancel"
                onClick={closeOtpPopup}
              />
              <SimpleButton
                label="Verify"
                type="submit"
                isDisabled={
                  otpMethods.formState.isSubmitting ||
                  !String(otpCode ?? "").trim()
                }
                isLoading={otpMethods.formState.isSubmitting}
              />
            </div>
          </form>
        </FormProvider>
      }
    />
    </>
  );
};

export default EditProfileModal;
